// Auth service for Cognito integration using AWS Amplify

import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser as amplifyGetCurrentUser,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
} from 'aws-amplify/auth';

const ALLOWED_ORG_IDS = ['clover', 'turk-tuborg'] as const;
const GROUP_PREFIX_TO_ORG_ID: Record<string, string> = {
  Clover: 'clover',
  TurkTuborg: 'turk-tuborg',
};

export type OrgRole = 'org_facilitator' | 'org_room_viewer';

export type AuthUser = {
  sub: string;
  email?: string;
  groups?: string[];
  /** Parsed from groups (Option B): true if Admin group present */
  isAdmin?: boolean;
  /** Org IDs the user may access (clover, turk-tuborg) */
  allowedOrgs?: string[];
  /** Role per org (only for non-Admin) */
  rolePerOrg?: Record<string, OrgRole>;
};

function parseGroupsToOrgContext(groups: string[]): { isAdmin: boolean; allowedOrgs: string[]; rolePerOrg: Record<string, OrgRole> } {
  const list = Array.isArray(groups) ? groups : [];
  if (list.includes('Admin')) {
    return { isAdmin: true, allowedOrgs: [...ALLOWED_ORG_IDS], rolePerOrg: {} };
  }
  const allowedOrgs: string[] = [];
  const rolePerOrg: Record<string, OrgRole> = {};
  for (const g of list) {
    const dash = g.indexOf('-');
    if (dash <= 0) continue;
    const prefix = g.slice(0, dash);
    const suffix = g.slice(dash + 1);
    const orgId = GROUP_PREFIX_TO_ORG_ID[prefix];
    if (!orgId || !ALLOWED_ORG_IDS.includes(orgId)) continue;
    const role: OrgRole | undefined =
      suffix === 'Facilitator' ? 'org_facilitator' : suffix === 'RoomViewer' ? 'org_room_viewer' : undefined;
    if (!role) continue;
    if (!allowedOrgs.includes(orgId)) allowedOrgs.push(orgId);
    const existing = rolePerOrg[orgId];
    if (!existing || (role === 'org_facilitator' && existing === 'org_room_viewer')) {
      rolePerOrg[orgId] = role;
    }
  }
  return { isAdmin: false, allowedOrgs, rolePerOrg };
}

/** True if user is in the Admin Cognito group (global admin; can access Admin UI). */
export const isGlobalAdmin = (user: AuthUser | null): boolean => {
  if (!user) return false;
  const groups = user.groups ?? [];
  return Array.isArray(groups) && groups.includes('Admin');
};

/** True if user has facilitator view (Admin or any *-Facilitator group). */
export const isAdmin = (user: AuthUser | null): boolean => {
  if (!user) return false;
  const groups = user.groups ?? [];
  const ctx = parseGroupsToOrgContext(groups);
  return ctx.isAdmin || Object.values(ctx.rolePerOrg).some((r) => r === 'org_facilitator');
};

/** True if user has only room-viewer access (no facilitator role in any org). */
export const isReadOnly = (user: AuthUser | null): boolean => {
  if (!user) return true;
  const groups = user.groups ?? [];
  const ctx = parseGroupsToOrgContext(groups);
  if (ctx.isAdmin) return false;
  const hasFacilitator = Object.values(ctx.rolePerOrg).some((r) => r === 'org_facilitator');
  return !hasFacilitator && ctx.allowedOrgs.length > 0;
};

/** Usernames that are read-only and must not be forced into TOTP setup (Cognito may still return TOTP_SETUP if pool is misconfigured). */
function getReadOnlyUsernames(): string[] {
  const raw = import.meta.env.VITE_READONLY_USERNAMES as string | undefined;
  if (!raw?.trim()) return ['viewer'];
  return raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
}

async function requiresMfaForUsername(username: string, isReadOnlyUser: boolean): Promise<boolean> {
  if (isReadOnlyUser) return false;
  try {
    const apiBase =
      import.meta.env.VITE_API_URL ||
      'https://r8570eovdl.execute-api.af-south-1.amazonaws.com/prod/api';
    const r = await fetch(
      `${apiBase}/auth/requires-mfa?username=${encodeURIComponent(username)}`
    );
    if (!r.ok) {
      return true;
    }
    const body = await r.json();
    return !!body?.requiresMfa;
  } catch {
    return true;
  }
}

async function hasTotpEnrolled(): Promise<boolean> {
  try {
    const pref = await fetchMFAPreference();
    const enabled = pref?.enabled ?? [];
    const preferred = pref?.preferred;
    const hasEnabledTotp = enabled.includes('TOTP');
    const hasPreferredTotp = preferred === 'TOTP';
    return hasEnabledTotp || hasPreferredTotp;
  } catch {
    return false;
  }
}

async function enforceMfaForSignedInUser(
  username: string,
  isReadOnlyUser: boolean
): Promise<{
  success: boolean;
  needsTotpSetupEnforced?: boolean;
}> {
  const requiresMfa = await requiresMfaForUsername(username, isReadOnlyUser);
  if (!requiresMfa) {
    return { success: true };
  }
  const totpEnrolled = await hasTotpEnrolled();
  if (totpEnrolled) {
    return { success: true };
  }
  return { success: false, needsTotpSetupEnforced: true };
}

// Configure Amplify - must be called before any auth (e.g. in main.tsx before render)
export const configureAuth = () => {
  // Bypass in development
  if (import.meta.env.DEV || !import.meta.env.VITE_COGNITO_USER_POOL_ID) {
    console.log('[AUTH] Development mode - using mock auth');
    return;
  }

  const region = import.meta.env.VITE_COGNITO_REGION || 'af-south-1';
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
        userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        loginWith: { email: true },
        ...(region && { region }),
      },
    },
  });
};

export const authService = {
  signIn: async (
    username: string,
    password: string,
    options?: { usePasswordAuth?: boolean }
  ): Promise<{
    success: boolean;
    needsMfa?: boolean;
    needsTotpSetup?: boolean;
    totpSetupUri?: string;
    /** Facilitator signed in but has not enrolled TOTP; app must force TOTP setup before allowing access. */
    needsTotpSetupEnforced?: boolean;
    needsNewPassword?: boolean;
    /** Room-viewer: Cognito asked for TOTP but account doesn't require it; prompt user to sign in again with new password. */
    passwordSetSignInAgain?: boolean;
    error?: string;
  }> => {
    const readOnlyUsernames = getReadOnlyUsernames();
    const isReadOnlyUser = readOnlyUsernames.includes(username.trim().toLowerCase());
    try {
      // Use USER_PASSWORD_AUTH for read-only users, or when requested (e.g. after TOTP setup to avoid SRP+MFA code mismatch).
      const usePasswordAuth = options?.usePasswordAuth ?? isReadOnlyUser;
      let result: Awaited<ReturnType<typeof amplifySignIn>>;
      if (usePasswordAuth) {
        try {
          result = await amplifySignIn({
            username,
            password,
            options: { authFlowType: 'USER_PASSWORD_AUTH' },
          });
        } catch (flowErr: unknown) {
          const msg = (flowErr as { message?: string })?.message ?? '';
          const isFlowNotAllowed =
            msg.includes('USER_PASSWORD_AUTH') ||
            msg.includes('not enabled') ||
            msg.includes('InvalidParameter') ||
            msg.includes('ExplicitAuthFlows');
          if (isFlowNotAllowed) {
            result = await amplifySignIn({ username, password });
          } else {
            throw flowErr;
          }
        }
      } else {
        result = await amplifySignIn({ username, password });
      }

      if (result.isSignedIn) {
        const enforcement = await enforceMfaForSignedInUser(username, isReadOnlyUser);
        return enforcement;
      }

      const step = result.nextStep?.signInStep ?? '';
      if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return { success: false, needsMfa: true };
      }
      if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        // Room-viewer-only users: ask backend (Cognito groups). Fallback to env list.
        let isReadOnlyUser = getReadOnlyUsernames().includes(username.trim().toLowerCase());
        try {
          const apiBase = import.meta.env.VITE_API_URL || 'https://r8570eovdl.execute-api.af-south-1.amazonaws.com/prod/api';
          const r = await fetch(`${apiBase}/auth/requires-mfa?username=${encodeURIComponent(username)}`);
          if (r.ok) {
            const body = await r.json();
            if (body?.requiresMfa === false) isReadOnlyUser = true;
          }
        } catch (_) {
          /* keep isReadOnlyUser from env list */
        }
        if (isReadOnlyUser) {
          try {
            await amplifySignOut();
          } catch (_) {
            /* ignore */
          }
          return { success: false, passwordSetSignInAgain: true };
        }
        const details = (result.nextStep as {
          totpSetupDetails?: { getSetupUri?: (appName: string, accountName?: string) => URL };
        })
          ?.totpSetupDetails;
        // Use email if available, otherwise username
        const accountName = (result.nextStep as { additionalInfo?: { email?: string } } | undefined)?.additionalInfo?.email || username;
        const uri =
          typeof details?.getSetupUri === 'function'
            ? details.getSetupUri('TableTop Exercise', accountName).toString()
            : undefined;
        return { success: false, needsTotpSetup: true, totpSetupUri: uri };
      }
      // First login with temporary password – user must set a new password
      if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' || String(step).includes('NEW_PASSWORD')) {
        return { success: false, needsNewPassword: true };
      }
      
      return { success: false, error: `Unexpected sign-in state: ${step}. If you use a temporary password, you may need to set a new password when prompted.` };
    } catch (error: any) {
      const errorMsg = error.message || String(error);
      // Only treat "already signed in" as success when we have a valid token for the same user.
      if (errorMsg.includes('already') && (errorMsg.includes('signed in') || errorMsg.includes('authenticated'))) {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          const hasToken =
            typeof idToken === 'string' ||
            (idToken && typeof (idToken as { getJwtToken?: () => string }).getJwtToken === 'function');
          if (hasToken) {
            await authService.syncTokenToStorage();
            const enforcement = await enforceMfaForSignedInUser(username, isReadOnlyUser);
            return enforcement;
          }
        } catch (_) {
          /* session invalid or expired */
        }
        // Stale partial session — clear and ask user to try again (do not loop signIn).
        try {
          await amplifySignOut();
        } catch (_) {
          /* ignore */
        }
        return { success: false, error: 'Session expired. Please sign in again.' };
      }
      console.error('Sign in error:', error);
      const isUserNotFound =
        errorMsg.includes('User does not exist') ||
        errorMsg.includes('UserNotFoundException');
      const isWrongCredentials =
        errorMsg.includes('Incorrect username or password') ||
        errorMsg.includes('NotAuthorizedException');
      let friendlySignInError: string;
      if (isUserNotFound) {
        friendlySignInError =
          'User not found. Check the username (case-sensitive). If you use a test user, ensure it was created in the same Cognito pool as this site (staging vs production).';
      } else if (isWrongCredentials) {
        friendlySignInError =
          'Incorrect username or password. Use the password you set after the temporary one (or ask an admin to reset your password).';
      } else {
        friendlySignInError = errorMsg || 'Sign in failed';
      }
      return { success: false, error: friendlySignInError };
    }
  },

  /** Complete sign-in after NEW_PASSWORD_REQUIRED; call with the new password. */
  confirmNewPassword: async (
    newPassword: string,
    username: string
  ): Promise<{
    success: boolean;
    needsMfa?: boolean;
    needsTotpSetup?: boolean;
    totpSetupUri?: string;
    needsTotpSetupEnforced?: boolean;
    error?: string;
    sessionExpired?: boolean;
  }> => {
    const readOnlyUsernames = getReadOnlyUsernames();
    const isReadOnlyUser = readOnlyUsernames.includes(username.trim().toLowerCase());

    const checkMfaEnrollment = async () => {
      const enforcement = await enforceMfaForSignedInUser(username, isReadOnlyUser);
      if (!enforcement.success && enforcement.needsTotpSetupEnforced) {
        return { success: false as const, needsTotpSetupEnforced: true };
      }
      return { success: true as const };
    };

    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      if (result.isSignedIn) {
        return checkMfaEnrollment();
      }
      const step = result.nextStep?.signInStep ?? '';
      if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return { success: false, needsMfa: true };
      }
      if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const details = (result.nextStep as {
          totpSetupDetails?: { getSetupUri?: (appName: string, accountName?: string) => URL };
        })?.totpSetupDetails;
        const uri =
          typeof details?.getSetupUri === 'function'
            ? details.getSetupUri('TableTop Exercise', username).toString()
            : undefined;
        return { success: false, needsTotpSetup: true, totpSetupUri: uri };
      }
      if (step.includes('NEW_PASSWORD')) {
        return { success: false, error: 'Password was not accepted. Please try again.' };
      }
      // Cognito may report a follow-on step while already authenticated enough for MFA checks.
      return checkMfaEnrollment();
    } catch (error: any) {
      console.error('Confirm new password error:', error);
      const msg = error?.message ?? String(error);
      const isFlowError =
        msg.includes('signIn was not called before confirmSignIn') ||
        msg.includes('page was refreshed during the sign in flow') ||
        msg.includes('session has expired') ||
        msg.includes('Session expired') ||
        msg.includes('Invalid session') ||
        msg.includes('An error occurred during the sign in process');
      if (isFlowError) {
        return {
          success: false,
          error:
            'Your password session expired. Please sign in again with your temporary password and set a new one.',
          sessionExpired: true,
        };
      }
      return { success: false, error: msg || 'Failed to set new password' };
    }
  },

  confirmMfa: async (code: string): Promise<{ success: boolean; error?: string; sessionExpired?: boolean }> => {
    try {
      await confirmSignIn({ challengeResponse: code });
      return { success: true };
    } catch (error: any) {
      console.error('MFA confirmation error:', error);
      const msg = error?.message ?? String(error);
      const isSessionExpired =
        msg.includes('session is expired') || msg.includes('Invalid session for the user');
      if (isSessionExpired) {
        return {
          success: false,
          error: 'Your login session expired. Please sign in again with your username and password.',
          sessionExpired: true,
        };
      }
      const isCodeMismatch =
        msg.includes('Code mismatch') ||
        msg.includes('EnableSoftwareTokenMFAException') ||
        msg.includes('CodeMismatch');
      const friendlyError = isCodeMismatch
        ? 'Code not accepted. Use the current 6-digit code (updates every 30 sec). If you have more than one TableTop entry, use the one you most recently added and check phone time. If it keeps failing, ask an admin to reset your MFA; then sign in again and set up the app once (delete old TableTop entries first).'
        : msg || 'Invalid MFA code';
      return { success: false, error: friendlyError };
    }
  },

  fetchMfaPreference: async (): Promise<{ enabled?: Array<'SMS' | 'TOTP' | 'EMAIL'>; preferred?: 'SMS' | 'TOTP' | 'EMAIL' } | null> => {
    try {
      return await fetchMFAPreference();
    } catch (error: any) {
      console.error('Fetch MFA preference error:', error);
      return null;
    }
  },

  startTotpSetup: async (
    appName: string,
    accountName?: string
  ): Promise<{ sharedSecret: string; setupUri: string }> => {
    const details = await setUpTOTP();
    return {
      sharedSecret: details.sharedSecret,
      setupUri: details.getSetupUri(appName, accountName).toString(),
    };
  },

  verifyTotpSetup: async (code: string): Promise<void> => {
    await verifyTOTPSetup({ code });
  },

  setTotpPreferred: async (): Promise<void> => {
    await updateMFAPreference({ totp: 'PREFERRED' });
  },

  signOut: async () => {
    try {
      await amplifySignOut();
      try {
        localStorage.removeItem('idToken');
      } catch (_) {}
    } catch (error) {
      console.error('Sign out error:', error);
    }
  },

  /**
   * Return a valid Cognito ID token, refreshing via Amplify when expired.
   * Prefer this over reading localStorage directly — cached tokens expire after 60 minutes.
   */
  getIdToken: async (opts?: { forceRefresh?: boolean }): Promise<string | null> => {
    if (import.meta.env.DEV || !import.meta.env.VITE_COGNITO_USER_POOL_ID) {
      try {
        return localStorage.getItem('idToken');
      } catch {
        return null;
      }
    }
    try {
      const session = await fetchAuthSession({ forceRefresh: !!opts?.forceRefresh });
      const idToken = session.tokens?.idToken;
      const tokenStr =
        typeof idToken === 'string'
          ? idToken
          : (idToken as { getJwtToken?: () => string })?.getJwtToken?.() ??
            (idToken as { toString?: () => string })?.toString?.() ??
            (idToken as { token?: string })?.token;
      if (tokenStr) {
        try {
          localStorage.setItem('idToken', tokenStr);
        } catch (_) {}
        return tokenStr;
      }
      return null;
    } catch (_) {
      try {
        return localStorage.getItem('idToken');
      } catch {
        return null;
      }
    }
  },

  /** Sync current session idToken to localStorage so api.ts can send it with requests. Returns AuthUser when session exists (for auth:changed so we can skip getCurrentUser and avoid browser credential prompt). */
  syncTokenToStorage: async (): Promise<AuthUser | null> => {
    if (import.meta.env.DEV || !import.meta.env.VITE_COGNITO_USER_POOL_ID) return null;
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const tokenStr =
        typeof idToken === 'string'
          ? idToken
          : (idToken as { getJwtToken?: () => string })?.getJwtToken?.() ??
            (idToken as { toString?: () => string })?.toString?.() ??
            (idToken as { token?: string })?.token;
      if (tokenStr) localStorage.setItem('idToken', tokenStr);
      const payload = idToken?.payload as {
        sub?: string;
        email?: string;
        'cognito:username'?: string;
        'cognito:groups'?: string[];
      } | undefined;
      if (payload?.sub) {
        const groups = payload['cognito:groups'] ?? [];
        const ctx = parseGroupsToOrgContext(groups);
        return {
          sub: payload.sub,
          email: payload.email || payload['cognito:username'],
          groups,
          isAdmin: ctx.isAdmin,
          allowedOrgs: ctx.allowedOrgs,
          rolePerOrg: ctx.rolePerOrg,
        };
      }
      return null;
    } catch (_) {
      return null;
    }
  },

  getCurrentUser: async (): Promise<AuthUser | null> => {
    // Bypass in development
    if (import.meta.env.DEV || !import.meta.env.VITE_COGNITO_USER_POOL_ID) {
      const groups = ['Admin'];
      const ctx = parseGroupsToOrgContext(groups);
      return {
        sub: 'dev-user-123',
        email: 'dev@localhost',
        groups,
        isAdmin: ctx.isAdmin,
        allowedOrgs: ctx.allowedOrgs,
        rolePerOrg: ctx.rolePerOrg,
      };
    }

    try {
      const user = await amplifyGetCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const tokenStr =
        typeof idToken === 'string'
          ? idToken
          : (idToken as { getJwtToken?: () => string })?.getJwtToken?.() ??
            (idToken as { toString?: () => string })?.toString?.() ??
            (idToken as { token?: string })?.token;
      if (tokenStr) {
        try {
          localStorage.setItem('idToken', tokenStr);
        } catch (_) {}
      }

      const groups = (idToken?.payload['cognito:groups'] as string[]) || [];
      const payload = idToken?.payload as {
        email?: string;
        'cognito:username'?: string;
      };
      const ctx = parseGroupsToOrgContext(groups);
      return {
        sub: user.userId,
        email: payload.email || payload['cognito:username'] || user.signInDetails?.loginId,
        groups,
        isAdmin: ctx.isAdmin,
        allowedOrgs: ctx.allowedOrgs,
        rolePerOrg: ctx.rolePerOrg,
      };
    } catch (error: unknown) {
      // Expected when not signed in (login page) - avoid spamming console
      const err = error as { message?: string; name?: string };
      const msg = err?.message ?? String(error);
      const name = err?.name ?? '';
      const isUnauthenticated =
        name === 'UserUnAuthenticatedException' ||
        msg.includes('UserUnAuthenticatedException') ||
        msg.includes('not authenticated') ||
        msg.includes('needs to be authenticated');
      if (!isUnauthenticated) {
        console.error('Get current user error:', error);
      }
      return null;
    }
  },
};
